import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { journalPrompts } from '../data/journalPrompts';
import axios from 'axios';
import '../styles/coastal-dusk.css';

const JournalingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentPrompt, setCurrentPrompt] = useState(0);
  const [responses, setResponses] = useState({});
  const [currentText, setCurrentText] = useState('');
  const [musicPlaying, setMusicPlaying] = useState(false);
  const [promptAudioPlaying, setPromptAudioPlaying] = useState(false);
  const [showAllExamples, setShowAllExamples] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(true);
  const [isTyping, setIsTyping] = useState(false);
  const [showTakeTimeWhisper, setShowTakeTimeWhisper] = useState(false);
  
  const musicRef = useRef(null);
  const promptAudioRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const whisperTimeoutRef = useRef(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Load saved progress
  useEffect(() => {
    loadSavedProgress();
    
    const playBackgroundMusic = async () => {
      if (musicRef.current) {
        try {
          musicRef.current.volume = 0.5;
          await musicRef.current.play();
          setMusicPlaying(true);
        } catch (err) {
          console.log('Autoplay blocked');
        }
      }
    };
    
    const timer = setTimeout(playBackgroundMusic, 100);
    return () => clearTimeout(timer);
  }, []);

  // Restart music when returning from video break
  useEffect(() => {
    if (location.state?.resumeAtPrompt !== undefined && location.state.resumeAtPrompt >= 4) {
      const restartMusic = async () => {
        if (musicRef.current) {
          try {
            musicRef.current.volume = 0.5;
            await musicRef.current.play();
            setMusicPlaying(true);
          } catch (err) {
            console.log('Music restart failed:', err);
          }
        }
      };
      
      setTimeout(restartMusic, 300);
      setTimeout(restartMusic, 1000);
    }
  }, [location.state?.resumeAtPrompt]);

  // Keep music playing
  useEffect(() => {
    const checkMusic = setInterval(() => {
      if (musicPlaying && musicRef.current && musicRef.current.paused) {
        musicRef.current.play().catch(e => console.log('Auto-resume failed:', e));
      }
    }, 3000);
    
    return () => clearInterval(checkMusic);
  }, [musicPlaying]);

  const loadSavedProgress = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      if (location.state?.resumeAtPrompt !== undefined) {
        setCurrentPrompt(location.state.resumeAtPrompt);
        setResponses(location.state.responses || {});
        setLoadingProgress(false);
        return;
      }

      const response = await axios.get(
        `${BACKEND_URL}/api/journaling/load-progress`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.has_progress) {
        setResponses(response.data.responses);
        setCurrentPrompt(response.data.current_prompt);
      }
    } catch (error) {
      console.error('Error loading progress:', error);
    } finally {
      setLoadingProgress(false);
    }
  };

  useEffect(() => {
    if (responses[currentPrompt]) {
      setCurrentText(responses[currentPrompt]);
    } else {
      setCurrentText('');
    }
    setShowAllExamples(false);
    
    if (musicRef.current && musicPlaying && musicRef.current.paused) {
      musicRef.current.play().catch(e => console.log('Music resume failed:', e));
    }
  }, [currentPrompt, responses, musicPlaying]);

  const handleMusicToggle = () => {
    if (!musicRef.current) return;
    
    if (musicPlaying) {
      musicRef.current.pause();
      setMusicPlaying(false);
    } else {
      musicRef.current.volume = 0.5;
      musicRef.current.play()
        .then(() => setMusicPlaying(true))
        .catch((err) => console.error('Failed to play music:', err));
    }
  };

  const handlePromptAudioToggle = () => {
    if (!promptAudioRef.current) return;
    
    if (promptAudioPlaying) {
      promptAudioRef.current.pause();
      promptAudioRef.current.currentTime = 0;
      setPromptAudioPlaying(false);
    } else {
      promptAudioRef.current.play()
        .then(() => setPromptAudioPlaying(true))
        .catch((err) => console.error('Failed to play prompt audio:', err));
    }
  };

  const handleExampleClick = (example) => {
    setCurrentText(prevText => prevText ? prevText + '\n\n' + example : example);
  };

  const handleNext = async () => {
    const updatedResponses = { ...responses, [currentPrompt]: currentText };
    setResponses(updatedResponses);

    if (currentPrompt === 3) {
      await saveProgressToBackend(updatedResponses, 4);
      
      if (musicRef.current) {
        musicRef.current.pause();
        setMusicPlaying(false);
      }
      
      navigate('/video-break', {
        state: { responses: updatedResponses, resumeAtPrompt: 4 }
      });
      return;
    }

    if (currentPrompt === journalPrompts.length - 1) {
      await clearProgress();
      navigate('/interpretation', { state: { responses: updatedResponses } });
    } else {
      const nextPrompt = currentPrompt + 1;
      setCurrentPrompt(nextPrompt);
      setCurrentText('');
      await saveProgressToBackend(updatedResponses, nextPrompt);
    }
  };

  const handleBack = () => {
    if (currentPrompt > 0) {
      const updatedResponses = { ...responses, [currentPrompt]: currentText };
      setResponses(updatedResponses);
      setCurrentPrompt(currentPrompt - 1);
    }
  };

  const saveProgressToBackend = async (updatedResponses, nextPrompt) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      await axios.post(
        `${BACKEND_URL}/api/journaling/save-progress`,
        { responses: updatedResponses, current_prompt: nextPrompt },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  };

  const clearProgress = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      await axios.delete(
        `${BACKEND_URL}/api/journaling/clear-progress`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Error clearing progress:', error);
    }
  };

  const prompt = journalPrompts[currentPrompt];
  const promptNumber = currentPrompt + 1;

  if (loadingProgress) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--grey-deep)' }}>
        <div className="landing-bg-image" style={{ backgroundImage: 'url(/assets/landing/bg-coastal.jpg)' }} />
        <div className="text-center relative z-10">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 mx-auto mb-4" style={{ borderColor: 'var(--accent-teal)' }}></div>
          <p className="text-body" style={{ fontSize: '1.1rem' }}>
            Loading your progress...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col relative" style={{ background: 'var(--grey-deep)' }}>
      {/* Blurred Background Image */}
      <div className="landing-bg-image" style={{ backgroundImage: 'url(/assets/landing/bg-coastal.jpg)' }} />
      
      {/* Gradient Orbs - Respond to typing */}
      <div 
        className={`landing-orb cool ${isTyping ? 'focus-shift' : ''}`} 
        style={{ '--orb-opacity': isTyping ? '0.55' : '0.3', top: '-5%', left: '-5%', width: '45vw', height: '45vw', maxWidth: '500px', maxHeight: '500px', transition: 'opacity 1.5s ease' }} 
      />
      <div 
        className={`landing-orb neutral ${isTyping ? 'focus-shift' : ''}`} 
        style={{ '--orb-opacity': isTyping ? '0.45' : '0.2', top: '45%', right: '-8%', width: '40vw', height: '40vw', maxWidth: '450px', maxHeight: '450px', animationDelay: '-5s', transition: 'opacity 1.5s ease' }} 
      />

      {/* Background Music */}
      <audio ref={musicRef} src="/assets/page2/background-music.mp3" loop preload="metadata" />

      {/* Prompt Audio */}
      <audio
        ref={promptAudioRef}
        key={promptNumber}
        src={`/assets/page2/prompts/prompt${promptNumber}.mp3`}
        preload="metadata"
        onEnded={() => setPromptAudioPlaying(false)}
      />

      {/* Header — Progress + Logout */}
      <div className="absolute top-6 left-6 right-6 z-50 flex justify-between items-center">
        {/* Progress Dots */}
        <div className="progress-dots">
          {[...Array(8)].map((_, idx) => (
            <span 
              key={idx} 
              className={`dot ${idx < currentPrompt ? 'completed' : ''} ${idx === currentPrompt ? 'current' : ''}`}
            />
          ))}
        </div>
        
        {/* Logout */}
        <button
          onClick={() => {
            localStorage.removeItem('token');
            navigate('/');
          }}
          className="listen-btn"
          style={{ marginBottom: 0 }}
          title="Your progress is automatically saved"
        >
          <span className="listen-label">logout</span>
        </button>
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-start overflow-y-auto pt-24 pb-8 px-6">
        <div className="w-full max-w-2xl space-y-8">
          {/* Prompt Text — Left aligned */}
          <div>
            <h2 
              style={{ fontFamily: 'var(--font-body)', fontWeight: 300, fontSize: 'clamp(1.4rem, 4vw, 1.9rem)', lineHeight: 1.35, color: 'var(--text-primary)', letterSpacing: '-0.01em', textAlign: 'left' }}
            >
              {prompt.text}
            </h2>
          </div>

          {/* Journaling Space */}
          <div className="space-y-3">
            <div className="flex items-center gap-3 mb-1">
              <label className="editorial-label" style={{ margin: 0, letterSpacing: '0.18em', color: 'rgba(163, 163, 163, 0.6)', fontSize: '0.65rem' }}>Your Journaling Space</label>
            </div>
            <div className="flex items-center gap-3 mb-2">
              {/* Audio Controls */}
              <button
                onClick={handlePromptAudioToggle}
                className={`listen-btn ${promptAudioPlaying ? 'playing' : ''}`}
                style={{ marginBottom: 0, minWidth: '120px', justifyContent: 'center' }}
                data-testid="toggle-prompt-audio-btn"
              >
                <span className="listen-icon">{promptAudioPlaying ? '◉◉◉' : '▶'}</span>
                <span className="listen-label">{promptAudioPlaying ? 'playing...' : 'listen'}</span>
              </button>
              <button
                onClick={handleMusicToggle}
                className="listen-btn"
                style={{ marginBottom: 0, minWidth: '90px', justifyContent: 'center' }}
                data-testid="toggle-music-btn"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 18V5l12-2v13"/>
                  <circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/>
                </svg>
                <span className="listen-label">{musicPlaying ? 'on' : 'off'}</span>
              </button>
            </div>
            <textarea
              value={currentText}
              onChange={(e) => {
                setCurrentText(e.target.value);
                setIsTyping(true);
                setShowTakeTimeWhisper(false);
                
                // Clear existing timeouts
                if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
                if (whisperTimeoutRef.current) clearTimeout(whisperTimeoutRef.current);
                
                // Set typing to false after 2s of inactivity
                typingTimeoutRef.current = setTimeout(() => setIsTyping(false), 2000);
                
                // Show "take your time" whisper after 60s if text > 20 chars
                if (e.target.value.length > 20) {
                  whisperTimeoutRef.current = setTimeout(() => setShowTakeTimeWhisper(true), 60000);
                }
              }}
              onFocus={() => setIsTyping(true)}
              onBlur={() => setIsTyping(false)}
              className="journaling-area"
              style={{ borderColor: 'rgba(184, 212, 208, 0.2)', boxShadow: '0 0 20px 4px rgba(184, 212, 208, 0.04)' }}
              placeholder="Write your thoughts here..."
              data-testid="journaling-textarea"
            />
            <p className={`take-time-whisper ${showTakeTimeWhisper ? 'visible' : ''}`}>
              Take your time...
            </p>
          </div>

          {/* Example Journaling */}
          <div className="space-y-4">
            <p className="editorial-label" style={{ color: 'rgba(163, 163, 163, 0.6)', fontSize: '0.65rem', letterSpacing: '0.18em' }}>Example Journaling Entry</p>
            <p style={{ fontFamily: 'var(--font-body)', fontWeight: 300, fontSize: '0.82rem', color: 'rgba(163, 163, 163, 0.5)', lineHeight: 1.6 }}>
              Select one or more examples to add them to your journaling space.
            </p>
            <div className="space-y-2">
              {prompt.examples.slice(0, showAllExamples ? prompt.examples.length : 3).map((example, idx) => (
                <button
                  key={idx}
                  onClick={() => handleExampleClick(example)}
                  className="w-full text-left py-4 px-5 rounded-lg transition-all group"
                  style={{ 
                    background: 'transparent',
                    borderLeft: '2px solid rgba(184, 212, 208, 0.15)',
                    color: '#ffffff',
                    fontFamily: 'var(--font-body)',
                    fontWeight: 300,
                    fontSize: 'clamp(1rem, 2.5vw, 1.15rem)',
                    lineHeight: '1.75'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderLeftColor = 'rgba(184, 212, 208, 0.5)'; e.currentTarget.style.background = 'rgba(184, 212, 208, 0.04)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderLeftColor = 'rgba(184, 212, 208, 0.15)'; e.currentTarget.style.background = 'transparent'; }}
                >
                  "{example}"
                </button>
              ))}
              {prompt.examples.length > 3 && !showAllExamples && (
                <button
                  onClick={() => setShowAllExamples(true)}
                  className="w-full text-center py-4"
                  style={{ 
                    fontFamily: 'var(--font-body)', fontWeight: 300, fontSize: '0.88rem',
                    color: 'var(--accent-teal)', letterSpacing: '0.04em',
                    background: 'none', border: 'none', cursor: 'pointer',
                    textDecoration: 'underline', textUnderlineOffset: '4px',
                    opacity: 0.8
                  }}
                >
                  Show {prompt.examples.length - 3} more examples
                </button>
              )}
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-center items-center gap-4 pt-4">
            {currentPrompt > 0 && (
              <button
                onClick={handleBack}
                className="cta-pill-landing"
                style={{ padding: '14px 28px', fontSize: '0.82rem' }}
              >
                Back
              </button>
            )}
            <button
              onClick={handleNext}
              className="cta-pill-landing"
              style={{ padding: '14px 32px', fontSize: '0.82rem' }}
              data-testid="next-prompt-btn"
            >
              {currentPrompt === journalPrompts.length - 1 ? 'Generate Interpretation' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JournalingPage;
